import { BarChart3, Users, Tag } from 'lucide-react';
import { usePancakeStore } from '../../store/pancake.store';
import { platformBadge } from './PancakeDashboardFilters';

function formatMinutes(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatCurrency(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function PancakeStatsCharts({ isDark }: { isDark: boolean }) {
  const { statsCampaigns, statsStaff, statsTags, statsEngagement } = usePancakeStore();

  const headerClass = `text-left px-4 py-3 font-medium text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`;
  const headerRightClass = `text-right px-4 py-3 font-medium text-xs uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`;
  const cellClass = `px-4 py-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;
  const cellRightClass = `px-4 py-3 text-right ${isDark ? 'text-slate-300' : 'text-gray-700'}`;
  const cellNameClass = `px-4 py-3 font-medium ${isDark ? 'text-white' : 'text-gray-900'}`;

  return (
    <div className="space-y-6">
      {/* Campaign Stats */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <BarChart3 className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Campaign</h3>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>({statsCampaigns.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={headerClass}>Campaign</th>
                <th className={headerRightClass}>Spend</th>
                <th className={headerRightClass}>Impressions</th>
                <th className={headerRightClass}>Clicks</th>
                <th className={headerRightClass}>CPC</th>
                <th className={headerRightClass}>CPM</th>
                <th className={headerClass}>Trang</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {statsCampaigns.length === 0 && (
                <tr>
                  <td colSpan={7} className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Chưa có dữ liệu campaign
                  </td>
                </tr>
              )}
              {statsCampaigns.map((c) => (
                <tr key={c.campaign_id} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={cellNameClass}>{c.campaign_name}</td>
                  <td className={cellRightClass}>{formatCurrency(c.spend)}</td>
                  <td className={cellRightClass}>{formatNumber(c.impressions)}</td>
                  <td className={cellRightClass}>{formatNumber(c.clicks)}</td>
                  <td className={cellRightClass}>{c.cpc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className={cellRightClass}>{c.cpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className={cellClass}>
                    <div className="flex items-center gap-2">
                      {platformBadge(c._platform)}
                      <span className="truncate max-w-[120px]">{c._pageName}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staff Performance */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <Users className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Nhân viên</h3>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>({statsStaff.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={headerClass}>Nhân viên</th>
                <th className={headerRightClass}>Inbox</th>
                <th className={headerRightClass}>Comment</th>
                <th className={headerRightClass}>Tổng</th>
                <th className={headerRightClass}>TB phản hồi</th>
                <th className={headerClass}>Trang</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {statsStaff.length === 0 && (
                <tr>
                  <td colSpan={6} className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Chưa có dữ liệu nhân viên
                  </td>
                </tr>
              )}
              {statsStaff.map((s) => (
                <tr key={`${s.user_id}-${s._pageId}`} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={cellNameClass}>{s.user_name}</td>
                  <td className={cellRightClass}>{formatNumber(s.inbox_count)}</td>
                  <td className={cellRightClass}>{formatNumber(s.comment_count)}</td>
                  <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{formatNumber(s.total)}</td>
                  <td className={cellRightClass}>{formatMinutes(s.average_response_time)}</td>
                  <td className={cellClass}>
                    <div className="flex items-center gap-2">
                      {platformBadge(s._platform)}
                      <span className="truncate max-w-[120px]">{s._pageName}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tag Stats */}
      <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
          <Tag className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
          <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Tags</h3>
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>({statsTags.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'bg-slate-800' : 'bg-gray-50'}>
                <th className={headerClass}>Tag</th>
                <th className={headerRightClass}>Số lượng</th>
                <th className={headerClass}>Trang</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-100'}`}>
              {statsTags.length === 0 && (
                <tr>
                  <td colSpan={3} className={`px-4 py-8 text-center ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    Chưa có dữ liệu tag
                  </td>
                </tr>
              )}
              {statsTags.map((t) => (
                <tr key={`${t.tag_id}-${t._pageId}`} className={`${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-gray-50'} transition-colors`}>
                  <td className={cellNameClass}>
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-slate-700 text-slate-200' : 'bg-gray-100 text-gray-700'}`}>
                      {t.tag_name}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    {formatNumber(t.count)}
                  </td>
                  <td className={cellClass}>
                    <div className="flex items-center gap-2">
                      {platformBadge(t._platform)}
                      <span className="truncate max-w-[120px]">{t._pageName}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Engagement Summary */}
      {statsEngagement.length > 0 && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`px-4 py-3 border-b flex items-center gap-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <BarChart3 className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Engagement theo trang</h3>
          </div>
          <div className="p-4 space-y-4">
            {statsEngagement.map((eng, idx) => (
              <div key={idx} className={`rounded-lg p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {platformBadge(eng._platform)}
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{eng._pageName}</span>
                </div>
                {eng.series.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {eng.series.map((s) => {
                      const total = s.data.reduce((sum, v) => sum + v, 0);
                      return (
                        <div key={s.name}>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{s.name}</p>
                          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatNumber(total)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
